import { useDisclosure } from "@mantine/hooks";
import { useFormikContext } from "formik";
import { useState } from "react";
import { t } from "ttag";
import * as Yup from "yup";

import { getErrorMessage } from "metabase/api/utils/errors";
import { ConfirmModal } from "metabase/common/components/ConfirmModal";
import {
  Form,
  FormErrorMessage,
  FormNumberInput,
  FormProvider,
  FormRadioGroup,
  FormSubmitButton,
  FormSwitch,
  FormTextInput,
} from "metabase/forms";
import { useSelector } from "metabase/redux";
import { useMaybeLocation } from "metabase/router";
import { getApplicationName } from "metabase/selectors/whitelabel";
import { Alert, Button, Group, Icon, Radio, Stack, Text } from "metabase/ui";
import * as Errors from "metabase/utils/errors";
import type {
  GitSyncAuthMethod,
  GitSyncConnectionCheck,
  GitSyncCredentials,
  GitSyncSettings,
  GitSyncType,
  UpdateGitSyncSettingsRequest,
} from "metabase-types/api";

import {
  useDisconnectGitSyncMutation,
  useTestGitSyncConnectionMutation,
  useUpdateGitSyncSettingsMutation,
} from "../api";
import { saveRepositoryDraft, takeRepositoryDraft } from "../utils";

import { ConnectGitHubApp } from "./ConnectGitHubApp";

const MIN_INTERVAL_MINUTES = 1;
const MAX_INTERVAL_MINUTES = 1440;
const HTTP_URL_PATTERN = /^https?:\/\/\S+$/i;
const INSTALLATION_ID_PATTERN = /^\d+$/;

export type RepositoryFormValues = {
  url: string;
  branch: string;
  auth_method: GitSyncAuthMethod;
  /** Empty keeps the saved token. */
  token: string;
  github_app_id: string;
  github_app_installation_id: string;
  /** Empty keeps the saved private key. */
  github_app_private_key: string;
  type: GitSyncType;
  auto_import: boolean;
  auto_import_interval: number;
  author_name: string;
  author_email: string;
};

const isGitHubApp = (authMethod: GitSyncAuthMethod) =>
  authMethod === "github-app";

const getSchema = (settings: GitSyncSettings) =>
  Yup.object({
    url: Yup.string()
      .required(Errors.required)
      .matches(HTTP_URL_PATTERN, () => t`must be an HTTPS URL`),
    branch: Yup.string().required(Errors.required),
    auth_method: Yup.mixed<GitSyncAuthMethod>().oneOf(["token", "github-app"]),
    token: Yup.string(),
    github_app_id: Yup.string().when("auth_method", {
      is: isGitHubApp,
      then: (schema) => schema.trim().required(Errors.required),
    }),
    github_app_installation_id: Yup.string().when("auth_method", {
      is: isGitHubApp,
      then: (schema) =>
        schema
          .trim()
          .required(Errors.required)
          .matches(INSTALLATION_ID_PATTERN, () => t`must be a number`),
    }),
    github_app_private_key: Yup.string().when("auth_method", {
      is: (authMethod: GitSyncAuthMethod) =>
        isGitHubApp(authMethod) && !settings.github_app_private_key_set,
      then: (schema) => schema.required(Errors.required),
    }),
    type: Yup.mixed<GitSyncType>().oneOf(["read-write", "read-only"]),
    auto_import: Yup.boolean(),
    auto_import_interval: Yup.number()
      .integer()
      .min(MIN_INTERVAL_MINUTES)
      .max(MAX_INTERVAL_MINUTES)
      .required(Errors.required),
    author_name: Yup.string(),
    author_email: Yup.string().email(Errors.email),
  });

const getInitialValues = (settings: GitSyncSettings): RepositoryFormValues => ({
  url: settings.url ?? "",
  branch: settings.branch,
  auth_method: settings.auth_method,
  token: "",
  github_app_id: settings.github_app_id ?? "",
  github_app_installation_id: settings.github_app_installation_id ?? "",
  github_app_private_key: "",
  type: settings.type,
  auto_import: settings.auto_import,
  auto_import_interval: settings.auto_import_interval,
  author_name: settings.author_name ?? "",
  author_email: settings.author_email ?? "",
});

/**
 * The credentials of the chosen method. Empty secrets are omitted so that the
 * saved ones are kept.
 */
export const getCredentials = (
  values: RepositoryFormValues,
): GitSyncCredentials =>
  isGitHubApp(values.auth_method)
    ? {
        auth_method: "github-app",
        github_app_id: values.github_app_id.trim(),
        github_app_installation_id: values.github_app_installation_id.trim(),
        github_app_private_key: values.github_app_private_key || undefined,
      }
    : {
        auth_method: "token",
        token: values.token || undefined,
      };

export const getUpdateRequest = (
  values: RepositoryFormValues,
): UpdateGitSyncSettingsRequest => ({
  url: values.url.trim(),
  branch: values.branch.trim(),
  ...getCredentials(values),
  type: values.type,
  auto_import: values.auto_import,
  auto_import_interval: values.auto_import_interval,
  author_name: values.author_name.trim() || null,
  author_email: values.author_email.trim() || null,
});

type RepositoryFormProps = {
  settings: GitSyncSettings;
};

/**
 * Connects the repository and sets how this instance syncs with it. Saving
 * checks that the repository can be reached first.
 */
export function RepositoryForm({ settings }: RepositoryFormProps) {
  const applicationName = useSelector(getApplicationName);
  const location = useMaybeLocation();
  const searchParams = new URLSearchParams(location?.search ?? "");
  const isGitHubAppInstalled = searchParams.get("github_app") === "installed";
  const gitHubAppError = searchParams.get("github_app_error");
  const [draft] = useState(() =>
    isGitHubAppInstalled || gitHubAppError != null
      ? takeRepositoryDraft()
      : null,
  );
  const [updateSettings] = useUpdateGitSyncSettingsMutation();
  const [savedCheck, setSavedCheck] = useState<GitSyncConnectionCheck | null>(
    null,
  );

  const handleSubmit = async (values: RepositoryFormValues) => {
    setSavedCheck(null);
    const response = await updateSettings(getUpdateRequest(values)).unwrap();
    setSavedCheck(response);
  };

  return (
    <FormProvider
      initialValues={{ ...getInitialValues(settings), ...draft }}
      validationSchema={getSchema(settings)}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ dirty, values }) => (
        <Form>
          <Stack gap="lg">
            {isGitHubAppInstalled && (
              <Alert color="success" icon={<Icon name="check" />}>
                {t`GitHub App connected. Enter the repository if you haven't, test the connection, then save.`}
              </Alert>
            )}
            {gitHubAppError != null && (
              <Alert color="error" icon={<Icon name="warning" />}>
                {t`Connecting GitHub failed: ${gitHubAppError}`}
              </Alert>
            )}
            <FormTextInput
              name="url"
              label={t`Repository URL`}
              description={t`The HTTPS address of the repository, as you would clone it.`}
              placeholder="https://github.com/your-org/metabase-content.git"
            />
            <FormTextInput
              name="branch"
              label={t`Branch`}
              description={t`The branch to export to and import from. The first export creates it if it doesn't exist.`}
              placeholder="main"
            />
            <RepositoryCredentialsFields
              settings={settings}
              authMethod={values.auth_method}
            />
            <FormRadioGroup name="type" label={t`This instance…`}>
              <Stack gap="sm" mt="xs">
                <Radio
                  value="read-write"
                  label={t`Edits synced content and exports it`}
                  description={t`For an instance where people build content to review and share through Git.`}
                />
                <Radio
                  value="read-only"
                  label={t`Only imports from Git`}
                  description={t`For a production instance: synced content can't be edited here, only updated from the branch.`}
                />
              </Stack>
            </FormRadioGroup>
            <FormSwitch
              name="auto_import"
              label={t`Import new commits on a schedule`}
            />
            {values.auto_import && (
              <FormNumberInput
                name="auto_import_interval"
                label={t`Check for new commits every (minutes)`}
                min={MIN_INTERVAL_MINUTES}
                max={MAX_INTERVAL_MINUTES}
                maw="16rem"
              />
            )}
            <Group grow align="flex-start">
              <FormTextInput
                name="author_name"
                label={t`Committer name`}
                placeholder={applicationName}
              />
              <FormTextInput
                name="author_email"
                label={t`Committer email`}
                placeholder="metabase@localhost"
              />
            </Group>
            {savedCheck && !dirty && (
              <ConnectionCheckResult
                check={savedCheck}
                branch={settings.branch}
                summary={getConnectedSummary(savedCheck)}
              />
            )}
            <RepositoryFormActions settings={settings} isDirty={dirty} />
          </Stack>
        </Form>
      )}
    </FormProvider>
  );
}

const GITHUB_APP_ENV_VARS = {
  github_app_id: "MB_REMOTE_SYNC_GITHUB_APP_ID",
  github_app_installation_id: "MB_REMOTE_SYNC_GITHUB_APP_INSTALLATION_ID",
  github_app_private_key: "MB_REMOTE_SYNC_GITHUB_APP_PRIVATE_KEY",
} as const satisfies Partial<Record<keyof RepositoryFormValues, string>>;

function RepositoryCredentialsFields({
  settings,
  authMethod,
}: {
  settings: GitSyncSettings;
  authMethod: GitSyncAuthMethod;
}) {
  const envVars = settings.github_app_env_vars;
  const isSetByEnv = (name: keyof typeof GITHUB_APP_ENV_VARS) =>
    envVars.includes(GITHUB_APP_ENV_VARS[name]);
  const envVarList = envVars.join(", ");
  const { values } = useFormikContext<RepositoryFormValues>();
  const handleLeave = () =>
    saveRepositoryDraft({ url: values.url, branch: values.branch });

  return (
    <Stack gap="lg">
      <FormRadioGroup name="auth_method" label={t`Sign in with`}>
        <Group gap="lg" mt="xs">
          <Radio
            value="token"
            label={t`An access token`}
            disabled={envVars.length > 0}
          />
          <Radio value="github-app" label={t`A GitHub App`} />
        </Group>
      </FormRadioGroup>
      {envVars.length > 0 && (
        <Text c="text-secondary">
          {t`Set by environment variables, which take precedence: ${envVarList}.`}
        </Text>
      )}
      {isGitHubApp(authMethod) ? (
        <>
          <ConnectGitHubApp settings={settings} onLeave={handleLeave} />
          <Text fw="bold">{t`Or enter the details of an existing App`}</Text>
          <Group grow align="flex-start">
            <FormTextInput
              name="github_app_id"
              label={t`App ID`}
              description={t`The App ID or Client ID on the App's settings page.`}
              disabled={isSetByEnv("github_app_id")}
            />
            <FormTextInput
              name="github_app_installation_id"
              label={t`Installation ID`}
              description={t`The number at the end of the installation's settings URL.`}
              disabled={isSetByEnv("github_app_installation_id")}
            />
          </Group>
          <FormTextInput
            name="github_app_private_key"
            type="password"
            autoComplete="new-password"
            label={t`Private key`}
            description={t`Paste the contents of the .pem file GitHub generated for the App. The App needs the repository permission Contents: Read and write (Read-only to only import) and must be installed on the repository. Works with github.com and GitHub Enterprise Server.`}
            placeholder={
              settings.github_app_private_key_set
                ? t`Saved. Leave empty to keep it.`
                : t`Paste the private key`
            }
            disabled={isSetByEnv("github_app_private_key")}
          />
        </>
      ) : (
        <FormTextInput
          name="token"
          type="password"
          autoComplete="new-password"
          label={t`Access token`}
          description={t`A personal or project access token that can read the repository and, to export, push to it. Leave it empty for a public repository you only import from.`}
          placeholder={
            settings.token_set
              ? t`Saved. Leave empty to keep it.`
              : t`Paste a token`
          }
        />
      )}
    </Stack>
  );
}

const getConnectedSummary = ({ branches }: GitSyncConnectionCheck) => {
  const branchList = branches.join(", ");
  return branches.length > 0
    ? t`Connected. Branches: ${branchList}.`
    : t`Connected. The repository is empty.`;
};

const getReachableSummary = ({ branches }: GitSyncConnectionCheck) => {
  const branchList = branches.join(", ");
  return branches.length > 0
    ? t`The repository can be reached. Branches: ${branchList}.`
    : t`The repository can be reached. It's empty.`;
};

function ConnectionCheckResult({
  check,
  branch,
  summary,
  hint,
}: {
  check: GitSyncConnectionCheck;
  branch: string;
  summary: string;
  hint?: string;
}) {
  return (
    <Alert color="success" icon={<Icon name="check" />}>
      <Stack gap="xs">
        <Text>{summary}</Text>
        {!check.branch_exists && (
          <Text>{t`The branch ${branch} doesn't exist yet. The first export creates it.`}</Text>
        )}
        {hint && <Text>{hint}</Text>}
      </Stack>
    </Alert>
  );
}

const getTestHint = (settings: GitSyncSettings, isDirty: boolean) => {
  if (!settings.enabled) {
    return t`Testing doesn't save anything. Click Connect to save the repository.`;
  }
  return isDirty
    ? t`Testing doesn't save anything. Click Save changes to use these settings.`
    : undefined;
};

function RepositoryFormActions({
  settings,
  isDirty,
}: {
  settings: GitSyncSettings;
  isDirty: boolean;
}) {
  const { values } = useFormikContext<RepositoryFormValues>();
  const [testConnection, testState] = useTestGitSyncConnectionMutation();
  const branch = values.branch.trim();
  const canTest = HTTP_URL_PATTERN.test(values.url.trim());

  const handleTest = () => {
    testState.reset();
    testConnection({
      url: values.url.trim(),
      branch: branch || undefined,
      type: values.type,
      ...getCredentials(values),
    });
  };

  return (
    <Stack gap="md">
      <Group gap="sm">
        <FormSubmitButton
          label={settings.enabled ? t`Save changes` : t`Connect`}
          variant="filled"
          disabled={settings.enabled && !isDirty}
        />
        <Button
          variant="default"
          loading={testState.isLoading}
          disabled={!canTest}
          onClick={handleTest}
        >
          {t`Test connection`}
        </Button>
        {settings.enabled && <DisconnectButton />}
      </Group>
      {testState.data && (
        <ConnectionCheckResult
          check={testState.data}
          branch={branch}
          summary={getReachableSummary(testState.data)}
          hint={getTestHint(settings, isDirty)}
        />
      )}
      {testState.error != null && (
        <Alert color="error" icon={<Icon name="warning" />}>
          {getErrorMessage(testState.error)}
        </Alert>
      )}
      <FormErrorMessage />
    </Stack>
  );
}

function DisconnectButton() {
  const [isOpened, { open, close }] = useDisclosure(false);
  const [disconnect, { error }] = useDisconnectGitSyncMutation();

  return (
    <>
      <Button variant="subtle" color="negative" onClick={open}>
        {t`Disconnect`}
      </Button>
      <ConfirmModal
        opened={isOpened}
        title={t`Disconnect the repository?`}
        message={t`Exports and imports stop. Collections stay marked as synced, so connecting again picks up where you left off.`}
        confirmButtonText={t`Disconnect`}
        confirmButtonProps={{ color: "negative" }}
        errorMessage={error != null ? getErrorMessage(error) : undefined}
        onConfirm={async () => {
          const result = await disconnect();
          if (!result.error) {
            close();
          }
        }}
        onClose={close}
      />
    </>
  );
}
